import { LEGACY_LOGIN_PATHS, PASSCODE_ROUTES } from '../navigation/paths';
import { tabStorage } from '../storage/deviceStorage';
// eslint-disable-next-line no-restricted-imports -- legacy: older code's keys are cleared when a session ends, until the backend asks retire them
import localStorageStore from '../store/localStorageStore';

const SESSION_CODES: ReadonlySet<string> = new Set([
  'ACCESS_TOKEN_EXPIRED',
  'PERMISSIONS_CHANGED',
  'NO_TOKEN',
  'INVALID_TOKEN',
  'SESSION_REVOKED',
  'REFRESH_REJECTED',
  'SESSION_ENDED',
]);

const SIGN_IN_PATHS: ReadonlySet<string> = new Set([PASSCODE_ROUTES.login, ...LEGACY_LOGIN_PATHS].map((path) => path.toLowerCase()));

const codeOf = (data: unknown): string | null => {
  const code = (data as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : null;
};

export const isSessionLoss = (status: number, data: unknown): boolean => {
  if (status !== 401) return false;
  const code = codeOf(data);
  return code === null || SESSION_CODES.has(code);
};

const isInAppPath = (path: string): boolean => path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

export const rememberSessionReturnPath = (): void => {
  const { pathname, search, hash } = window.location;
  if (SIGN_IN_PATHS.has(pathname.toLowerCase())) return;
  tabStorage.write('returnPath', `${pathname}${search}${hash}`);
};

export const readSessionReturnPath = (): string | null => {
  const path = tabStorage.read('returnPath');
  return path !== null && isInAppPath(path) ? path : null;
};

export const clearSessionReturnPath = (): void => {
  tabStorage.remove('returnPath');
};

type SessionEndedListener = () => void;

const sessionEndedListeners = new Set<SessionEndedListener>();

/** The server turned this tab's session away mid-use; whoever holds the session state follows. */
export const onSessionEnded = (listener: SessionEndedListener): (() => void) => {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
};

export const announceSessionEnded = (): void => {
  sessionEndedListeners.forEach((listener) => listener());
};

export const endLocalSession = (): void => {
  rememberSessionReturnPath();
  announceSessionEnded();
  // eslint-disable-next-line no-restricted-properties -- legacy: older code's keys are cleared when a session ends, until the backend asks retire them
  localStorageStore.logout();
  window.location.replace(PASSCODE_ROUTES.login);
};

type PermissionsListener = () => void;

const permissionsListeners = new Set<PermissionsListener>();

export const onPermissionsChanged = (listener: PermissionsListener): (() => void) => {
  permissionsListeners.add(listener);
  return () => {
    permissionsListeners.delete(listener);
  };
};

export const announcePermissionsChanged = (): void => {
  permissionsListeners.forEach((listener) => listener());
};

export interface AccessRefusal {
  readonly url: string;
  readonly code: string | null;
}

type RefusalListener = (refusal: AccessRefusal) => void;

const refusalListeners = new Set<RefusalListener>();

const UNWATCHED_PATHS: readonly string[] = ['/v2/permissions/', '/v2/auth/'];

export const onAccessRefused = (listener: RefusalListener): (() => void) => {
  refusalListeners.add(listener);
  return () => {
    refusalListeners.delete(listener);
  };
};

export const announceAccessRefused = (url: string | undefined, data: unknown): void => {
  const path = url ?? '';
  if (UNWATCHED_PATHS.some((prefix) => path.includes(prefix))) return;
  const refusal: AccessRefusal = { url: path, code: codeOf(data) };
  refusalListeners.forEach((listener) => listener(refusal));
};
